import { signOut } from "aws-amplify/auth";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { unsubscribeFromPush } from "../helper/pushNotifications";

interface LogoutStore {
  isLoading: boolean;
  logout: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
}

interface Loading {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface Notification {
  newOrderReceived: boolean;
  setNewOrderReceived: (received: boolean) => void;
  type: string;
  notificationMessage: string;
  isNotificationShow: boolean;
  setType: (type: string) => void;
  setIsNotificationShow: (show: boolean) => void;
  setNotificationMessage: (msg: string) => void;
}

interface AppShell {
  sidebarOpen: number;
  isAsideShow: boolean;
  isOpen: boolean;
  isSidebarCollapsed: boolean;
  isDrawerOpen: boolean;
  drawerComponentElements: any;
  setDrawerComponentElements: (elements: any) => void;
  setIsDrawerOpen: (open: boolean) => void;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  setIsAsideShow: (loading: boolean) => void;
  setIsOpen: (open: boolean) => void;
  setSidebarOpen: (tab: number) => void;
}

interface RestaurantId {
  roles: any[];
  restaurant: any;
  restaurantId: string;
  brandId: string;
  userId: string;
  selectedForm: string;
  currentOrderOpenInPOS: any;
  userRole: any;
  userName: string;
  inrolledRestaurants: any[];
  setInrolledRestaurants: (inrolled: any[]) => void;
  setUserName: (name: string) => void;
  setRoles: (userRoles: any) => void;
  setRestaurant: (data: any) => void;
  setRestaurantId: (id: string) => void;
  setUserId: (id: string) => void;
  setSelectedForm: (id: string) => void;
  setCurrentOrderOpenInPOS: (order: any) => void;
  setBrandId: (id: string) => void;
  setUserRole: (role: any) => void;
  switchBranch: (branch: any) => void;
}

export const useRestaurant = create<RestaurantId>()(
  persist(
    (set) => ({
      roles: [],
      restaurant: {},
      restaurantId: "",
      brandId: "",
      userId: "",
      selectedForm: "",
      currentOrderOpenInPOS: null,
      userRole: null,
      userName: "",
      setUserName: (name: string) => {
        set({ userName: name });
      },
      inrolledRestaurants: [],
      setInrolledRestaurants: (restaurantIds: any[]) => {
        set({ inrolledRestaurants: restaurantIds });
      },
      setRoles: (userRoles: any) => {
        set({ roles: userRoles });
      },
      setRestaurant: (data: any) => {
        set({ restaurant: data });
      },
      setRestaurantId: (id: string) => {
        set({ restaurantId: id });
      },
      setUserId: (id: string) => {
        set({ userId: id });
      },
      setSelectedForm: (id: string) => {
        set({ selectedForm: id });
      },
      setCurrentOrderOpenInPOS: (order: any) => {
        set({ currentOrderOpenInPOS: order || null });
      },
      setBrandId: (id: string) => {
        set({ brandId: id });
      },
      setUserRole: (role: any) => {
        set({ userRole: role });
      },
      switchBranch: (branch: any) => {
        set({
          restaurant: branch,
          restaurantId: branch.restaurantId,
          currentOrderOpenInPOS: null, // Clear any open POS orders when switching branches
        });
        // Zustand persistence handles localStorage via restaurant-storage
      },
    }),
    {
      name: "restaurant-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useLogoutStore = create<LogoutStore>((set) => ({
  isLoading: false,
  logout: async () => {
    set({ isLoading: true });
    try {
      // Clean up push notifications subscription on sign-out
      try {
        await unsubscribeFromPush();
      } catch (err) {
        console.warn("Failed to unsubscribe from push on logout:", err);
      }

      await signOut();
      // Hub will emit signedOut event which updates useAuthStatus
    } finally {
      set({ isLoading: false });
    }
  },
  setIsLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));

export const useLoading = create<Loading>((set) => ({
  isLoading: false,
  setIsLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));

export const useNotificationWindow = create<Notification>((set) => ({
  newOrderReceived: false,
  isNotificationShow: false,
  type: "sucess",
  notificationMessage: "",
  setType: (type: string) => {
    set({ type: type });
  },
  setNewOrderReceived: (received: boolean) => {
    set({ newOrderReceived: received });
  },
  setIsNotificationShow: (show: boolean) => {
    set({ isNotificationShow: show });
    setTimeout(() => {
      set({ isNotificationShow: false });
    }, 4000);
  },
  setNotificationMessage: (msg: string) => {
    set({ notificationMessage: msg });
    setTimeout(() => {
      set({ notificationMessage: "" });
    }, 5000);
  },
}));

export const useAppShellLayout = create<AppShell>()(
  persist(
    (set) => ({
      sidebarOpen: 0,
      isAsideShow: false,
      isOpen: true,
      isSidebarCollapsed: false,
      isDrawerOpen: false,
      drawerComponentElements: [],
      setSidebarOpen: (tab: number) => {
        set({ sidebarOpen: tab });
      },
      setDrawerComponentElements: (elements: any) => {
        set({ drawerComponentElements: elements });
      },
      setIsDrawerOpen: (open: boolean) => {
        set({ isDrawerOpen: open });
      },
      setIsSidebarCollapsed: (collapsed: boolean) => {
        set({ isSidebarCollapsed: collapsed });
      },
      setIsAsideShow: (visibility: boolean) => {
        set({ isAsideShow: visibility });
      },
      setIsOpen: (open: boolean) => {
        set({ isOpen: open });
      },
    }),
    {
      name: "app-shell-layout-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
// Order History
interface OrderState {
  selection: string[];
  selectedOrderDetails: any | null; // Replace with your Order type if known
  toggleRow: (orderId: string, orderList: any[]) => void;
  toggleAll: (orderList: any[]) => void;
  resetSelection: () => void;
}

export const useOrderFilterStore = create<any>((set: any) => ({
  searchText: "",
  selectedOrderId: null, // Default value, no order selected initially
  dateRange: [null, null], // default value
  orderStatus: "",
  setOrderStatus: (status: string) => set({ orderStatus: status }),
  setDateRange: (range: any) => set({ dateRange: range }),
  resetDateRange: () => set({ dateRange: [null, null] }),
  setSearchText: (text: any) => set({ searchText: text }),
  setSelectedOrderId: (orderId: any) => set({ selectedOrderId: orderId }),
}));
